import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useNotification } from '../contexts/NotificationContext';

/**
 * 알림 보관함 종 아이콘 전용 미확인 알림 여부 확인 훅
 * - 조건: recipient_id = 현재 사용자, is_read = false, 최근 30일
 * - 차단 필터: friendships(status='blocked') 조회 후 actor_id 가 차단 목록에 있는 알림 제외 (Set 방식)
 * - NotificationContext의 notifSignal 변경 시 자동 재조회
 */
export function useUnreadNotificationCheck(userId) {
  const { notifSignal } = useNotification();
  const [hasUnread, setHasUnread] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const checkUnread = async () => {
      if (!userId) {
        if (isMounted) setHasUnread(false);
        return;
      }

      try {
        const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();

        // 1) 최근 30일간 내 미확인 알림 조회 (최소 컬럼: id, actor_id)
        // 2) 내 차단 목록 조회
        const [notifResult, blockedResult] = await Promise.all([
          supabase
            .from('notifications')
            .select('id, actor_id')
            .eq('recipient_id', userId)
            .eq('is_read', false)
            .gte('created_at', thirtyDaysAgo),
          supabase
            .from('friendships')
            .select('friend_id')
            .eq('user_id', userId)
            .eq('status', 'blocked'),
        ]);

        if (notifResult.error) {
          console.error('[useUnreadNotificationCheck] 알림 조회 실패:', notifResult.error);
          return;
        }

        const blockedSet = new Set((blockedResult.data || []).map((r) => r.friend_id));
        const activeUnread = (notifResult.data || []).some(
          (n) => !n.actor_id || !blockedSet.has(n.actor_id)
        );

        if (isMounted) {
          setHasUnread(activeUnread);
        }
      } catch (err) {
        console.error('[useUnreadNotificationCheck] 조회 예외 발생:', err);
      }
    };

    checkUnread();

    return () => {
      isMounted = false;
    };
  }, [userId, notifSignal]);

  return hasUnread;
}
